import { Col, Row } from 'antd';
import { FieldType, FormType } from 'enums/FieldTypeEnums';
import React, { FC, ReactNode } from 'react';
import InputField, { InputFieldProps } from '../InputField';
import COSelect, { SelectProps } from '../COSelect';
import COCheckbox, { CheckboxInputProps } from '../COCheckbox';
import { Gutter } from 'antd/lib/grid/row';
import clsx from 'clsx';
import styles from './fieldsRow.module.scss';
import SwitchComponent, { SwitchComponentProps } from '../SwitchComponent';
import RHFInputField from '../ReactHookFormFields/RHFInputField';
import RHFSelect from '../ReactHookFormFields/RHFSelect';
import RHFCheckbox from '../ReactHookFormFields/RHFCheckbox';
import RHFSwitch from '../ReactHookFormFields/RHFSwitch';
import LocationModalInputField, {
  LocationModalInputFieldProps,
} from '../LocationModalInputField';
import FeedLocationModalInputField, {
  FeedLocationModalInputFieldProps,
} from 'views/LiveStockCreationFlow/LiveStockFeed/FeedTransportation/FeedFields/feedMultipleOrigins';

export interface FieldProps {
  type: FieldType;
  fieldProps?:
    | InputFieldProps
    | SelectProps
    | CheckboxInputProps
    | SwitchComponentProps
    | LocationModalInputFieldProps
    | FeedLocationModalInputFieldProps;
  additionalElement?: ReactNode;
  span?: number;
  className?: string;
}

export interface FieldsRowProps {
  data?: FieldProps[];
  rowClassName?: string;
  rowGutter?: Gutter;
  formType?: FormType;
}

//Mandatory to wrap the FieldsRow inside a Formik or FormProvider based on the type of form
const FieldsRow: FC<FieldsRowProps> = ({
  data,
  rowClassName,
  rowGutter,
  //By default Formik would be taken. For the scope 3 formType needs to be specified explicitly (Check app components)
  formType = FormType.FORMIK,
}) => {
  const getAppropriateField = (field: FieldProps) => {
    const { type, fieldProps, additionalElement } = field;
    if (formType === FormType.FORMIK)
      switch (type) {
        case FieldType.INPUT:
          return <InputField {...(fieldProps as InputFieldProps)} />;

        case FieldType.SELECT:
          return <COSelect {...(fieldProps as SelectProps)} />;

        case FieldType.CHECKBOX:
          return <COCheckbox {...(fieldProps as CheckboxInputProps)} />;

        case FieldType.SWITCH:
          return <SwitchComponent {...(fieldProps as SwitchComponentProps)} />;

        case FieldType.ELEMENT:
          return additionalElement;

        default:
          return null;
      }
    else if (formType === FormType.REACT_HOOK_FORMS)
      switch (type) {
        case FieldType.INPUT:
          return <RHFInputField {...(fieldProps as InputFieldProps)} />;

        case FieldType.SELECT:
          return <RHFSelect {...(fieldProps as SelectProps)} />;

        case FieldType.CHECKBOX:
          return <RHFCheckbox {...(fieldProps as CheckboxInputProps)} />;

        case FieldType.SWITCH:
          return <RHFSwitch {...(fieldProps as SwitchComponentProps)} />;

        case FieldType.LOCATION_MODAL:
          return (
            <LocationModalInputField
              {...(fieldProps as LocationModalInputFieldProps)}
            />
          );
        case FieldType.FEED_LOCATION_MODAL:
          return (
            <FeedLocationModalInputField
              {...(fieldProps as FeedLocationModalInputFieldProps)}
            />
          );

        case FieldType.ELEMENT:
          return additionalElement;

        default:
          return null;
      }
  };
  return (
    <Row
      gutter={rowGutter ?? [25, 25]}
      className={clsx(rowClassName, styles.fieldsRow)}>
      {data?.map((item, index) => (
        <Col
          span={item?.span}
          key={`${item.type}-${index}`}
          className={clsx(item?.className && styles[item.className])}>
          {getAppropriateField(item)}
        </Col>
      ))}
    </Row>
  );
};

export default FieldsRow;
